import type { CSSProperties } from "react";

/**
 * Estilos tipográficos do Workbase DS (tokens `--woba-typography-*`) para as telas
 * montadas com componentes locais (sidebar do shell WOS, Centro de ações com IA),
 * quando não há um componente do DS que entregue o texto pronto. Os fallbacks
 * reproduzem os valores do tema claro, para o texto não colapsar sem os tokens.
 */
const typography = (
  name: string,
  fallback: {
    family: string;
    size: string;
    weight: number;
    lineHeight: string;
    letterSpacing?: string;
  },
): CSSProperties => ({
  fontFamily: `var(--woba-typography-${name}-font-family, ${fallback.family})`,
  fontSize: `var(--woba-typography-${name}-font-size, ${fallback.size})`,
  fontWeight: `var(--woba-typography-${name}-font-weight, ${fallback.weight})`,
  lineHeight: `var(--woba-typography-${name}-line-height, ${fallback.lineHeight})`,
  letterSpacing: `var(--woba-typography-${name}-letter-spacing, ${
    fallback.letterSpacing ?? "0px"
  })`,
});

const BASE = '"Manrope", sans-serif';
const HEADING = '"Host Grotesk", sans-serif';

export const DS_TYPOGRAPHY = {
  /** Label/Xsmall: itens de menu, chips, botões textuais pequenos. */
  labelXsmall: typography("label-xsmall", {
    family: BASE,
    size: "14px",
    weight: 700,
    lineHeight: "14px",
  }),
  /** Label/2xsmall: selos e rótulos da navegação inferior. */
  label2xsmall: typography("label-2xsmall", {
    family: BASE,
    size: "12px",
    weight: 700,
    lineHeight: "12px",
  }),
  /** Label/Small: título do Header mobile. */
  labelSmall: typography("label-small", {
    family: BASE,
    size: "16px",
    weight: 700,
    lineHeight: "16px",
  }),
  /** Label/Input Xsmall: texto digitado e placeholder de campos. */
  labelInputXsmall: typography("label-input-xsmall", {
    family: BASE,
    size: "14px",
    weight: 500,
    lineHeight: "20px",
  }),
  /** Caption/Medium: descrições secundárias (cargo, plano). */
  captionMedium: typography("caption-medium", {
    family: BASE,
    size: "14px",
    weight: 500,
    lineHeight: "20px",
  }),
  /** Overline/Medium: rótulos de seção em maiúsculas. */
  overlineMedium: {
    ...typography("overline-medium", {
      family: BASE,
      size: "12px",
      weight: 600,
      lineHeight: "12px",
      letterSpacing: "1px",
    }),
    textTransform: "uppercase",
  } satisfies CSSProperties,
  /** Subheading/Medium: nome do produto no cabeçalho da sidebar. */
  subheadingMedium: typography("subheading-medium", {
    family: HEADING,
    size: "16px",
    weight: 400,
    lineHeight: "20px",
  }),
  /** Heading/Medium: título do Centro de ações. */
  headingMedium: typography("heading-medium", {
    family: HEADING,
    size: "24px",
    weight: 600,
    lineHeight: "28px",
  }),
  /** Paragraph/Medium: subtítulo do Centro de ações. */
  paragraphMedium: typography("paragraph-medium", {
    family: BASE,
    size: "16px",
    weight: 500,
    lineHeight: "24px",
  }),
} as const;

/** Cores semânticas do Workbase DS usadas nas mesmas telas. */
export const DS_COLOR = {
  contentDefault: "var(--woba-color-content-default, #000)",
  contentSubtle: "var(--woba-color-content-subtle, rgba(0,0,0,0.64))",
  contentActive: "var(--woba-color-content-common-active, #000)",
  strokeDefault: "var(--woba-color-stroke-default, rgba(0,0,0,0.08))",
  backgroundDefault: "var(--woba-color-background-default, #fff)",
  surfaceDefault: "var(--woba-color-surface-default, #fff)",
  surfaceHover: "var(--woba-color-surface-common-hover, rgba(0,0,0,0.08))",
  surfaceDisabled: "var(--woba-color-surface-common-disabled, #f2f2f2)",
  /** Pílula do item ativo (Sidebar/Menu Item do DS). */
  surfaceActive:
    "var(--woba-color-surface-accent-secondary-subtle-on-subtle, #ebe1cb)",
  surfaceAccentSecondary: "var(--woba-color-surface-accent-secondary, #f2edde)",
} as const;
